import React, {useState} from 'react';
import {Modal, StyleSheet, View} from 'react-native';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {deleteStreamSource} from '@/entities/stream/api';
import {messageOf} from '@/shared/lib/errors';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * Delete a camera — with its settings, commands and access token. The token
 * stops working at once, so the camera cannot come back under this entry; to
 * use it again it has to be added anew and given a new token.
 */
const styles = StyleSheet.create({
  root: {gap: spacing(3)},
  backdrop: {flex: 1, justifyContent: 'center', padding: spacing(5)},
  dialog: {borderRadius: radii.xl, padding: spacing(5), gap: spacing(4)},
  row: {flexDirection: 'row', gap: spacing(2)},
  flex: {flex: 1},
});

export function DeleteCameraPanel({
  companyId,
  sourceId,
  name,
  onDeleted,
}: {
  companyId: string;
  sourceId: string;
  name: string;
  onDeleted: () => void;
}) {
  const {t} = useTranslation();
  const p = useTheme();
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);

  const remove = useMutation({
    mutationFn: () => deleteStreamSource(companyId, sourceId),
    onSuccess: () => {
      setConfirm(false);
      void qc.invalidateQueries({queryKey: ['stream-cameras', companyId]});
      // The source row is a device too: the never-connected list and the device
      // counts read it from there.
      void qc.invalidateQueries({queryKey: ['devices']});
      onDeleted();
    },
  });

  return (
    <View style={styles.root}>
      <AppButton
        title={t('camera.delete.action')}
        tone="destructive"
        onPress={() => setConfirm(true)}
      />
      {remove.isError ? (
        <Banner tone="error" title={messageOf(remove.error, t('errors.unknown'))} />
      ) : null}

      <Modal
        visible={confirm}
        transparent
        animationType="fade"
        onRequestClose={() => setConfirm(false)}>
        <View style={[styles.backdrop, {backgroundColor: p.overlay}]}>
          <View style={[styles.dialog, {backgroundColor: p.elevated}]}>
            <AppText variant="title">{t('camera.delete.confirmTitle', {name})}</AppText>
            <AppText variant="body" color={p.mutedForeground}>
              {t('camera.delete.confirmBody')}
            </AppText>
            <View style={styles.row}>
              <AppButton
                title={t('actions.cancel')}
                tone="outline"
                style={styles.flex}
                onPress={() => setConfirm(false)}
              />
              <AppButton
                title={t('camera.delete.action')}
                tone="destructive"
                style={styles.flex}
                loading={remove.isPending}
                onPress={() => remove.mutate()}
              />
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}
